import { Link } from "react-router-dom";

interface ArticleCardProps {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  author: string;
  publishDate: string;
  readCount: number;
  commentCount: number;
  isHot?: boolean;
  isTop?: boolean;
  imageUrl: string;
  tags: string[];
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({
  title,
  slug,
  excerpt,
  category,
  categorySlug,
  author,
  publishDate,
  readCount,
  commentCount,
  isHot,
  isTop,
  imageUrl,
  variant = "default",
}: ArticleCardProps) {
  const formatCount = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return n.toLocaleString();
  };

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-16 md:w-24 md:h-[72px] shrink-0 rounded-md overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
            {isHot && (
              <span className="inline-flex items-center gap-1 mr-1.5 text-primary-500">
                <i className="ri-fire-line" />
              </span>
            )}
            {title}
          </h4>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
            <span>{publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatCount(readCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        </div>
        <div className="p-4 md:p-5">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              {category}
            </span>
            {isHot && (
              <span className="inline-flex items-center gap-1 text-xs text-primary-500 font-medium">
                <i className="ri-fire-line" />
                热门
              </span>
            )}
            {isTop && (
              <span className="inline-flex items-center gap-1 text-xs text-accent-600 font-medium">
                <i className="ri-pushpin-line" />
                置顶
              </span>
            )}
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {excerpt}
          </p>
          <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
            <span>{author}</span>
            <span>{publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatCount(readCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-3-line" />
              {commentCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all"
    >
      <div className="w-full sm:w-40 md:w-48 h-48 sm:h-28 md:h-32 shrink-0 rounded-lg overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
              {category}
            </span>
            {isHot && (
              <span className="inline-flex items-center gap-1 text-xs text-primary-500">
                <i className="ri-fire-line" />
              </span>
            )}
          </div>
          <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {title}
          </h3>
          <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2 leading-relaxed hidden sm:block">
            {excerpt}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
          <span>{author}</span>
          <span>{publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatCount(readCount)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-message-3-line" />
            {commentCount}
          </span>
        </div>
      </div>
    </Link>
  );
}